import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV } from "@/lib/content";
import { LogoWordmark } from "@/components/common/Logo";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "glass shadow-[0_10px_40px_rgba(0,0,0,0.4)]" : "bg-transparent"}`}
      data-testid="site-header"
    >
      <div className="container-px flex h-[72px] items-center justify-between">
        <Link to="/" data-testid="nav-logo-link" aria-label="Pixel-Hatch home">
          <LogoWordmark />
        </Link>

        <nav className="hidden items-center gap-1 xl:flex">
          {NAV.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-link-${item.path === "/" ? "home" : item.path.slice(1)}`}
              className={`whitespace-nowrap rounded-full px-3 py-2 text-[13px] font-medium transition-colors ${
                pathname === item.path ? "text-orange-400" : "text-zinc-300 hover:text-white"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <button onClick={() => navigate("/contact")} className="btn-primary" data-testid="nav-cta-consultation">
            Book a Free Consultation <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        <button
          className="rounded-lg p-2 text-white xl:hidden"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="glass overflow-hidden xl:hidden"
            data-testid="mobile-menu"
          >
            <div className="container-px flex flex-col gap-1 py-4">
              {NAV.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-link-${item.path === "/" ? "home" : item.path.slice(1)}`}
                  className={`rounded-lg px-4 py-3 text-sm font-medium ${pathname === item.path ? "bg-white/5 text-orange-400" : "text-zinc-300"}`}
                >
                  {item.label}
                </Link>
              ))}
              <button onClick={() => navigate("/contact")} className="btn-primary mt-3 justify-center" data-testid="mobile-cta-consultation">
                Book a Free Consultation <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
